// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
export const PCF_SUPERVISION_WORDS = {
    // ── Generic values ──────────────────────────────────────────
    none: { en: '—', vi: '—' },
    yes: { en: 'Yes', vi: 'Có' },
    no: { en: 'No', vi: 'Không' },
    notStated: { en: 'Not stated', vi: 'Chưa nêu' },
    notRecorded: { en: 'Not recorded', vi: 'Chưa ghi nhận' },
    fieldNotes: { en: 'Notes', vi: 'Ghi chú' },
    noLaterRequest: { en: 'None', vi: 'Không có' },
    /**
     * F6 (fix sprint #683): a linked record the server could not resolve in the
     * organization's own records — named from the record as read, never from an id.
     */
    referenceNotFound: { en: 'No longer found', vi: 'Không còn tìm thấy' },
    // ── Register notices ────────────────────────────────────────
    noDeclaringFramework: {
        en: 'No installed framework declares this register, so no record can be created. This describes the content installed for your organization, not whether your organization holds the duty.',
        vi: 'Không có khung tuân thủ đã cài đặt nào khai báo sổ này nên không thể tạo hồ sơ. Câu này mô tả nội dung đã cài đặt cho tổ chức bạn, không phải việc tổ chức bạn có nghĩa vụ hay không.',
    },
    applicabilityOutOfScope: {
        en: 'On your organization’s stated profile, this is not your organization’s duty. The register stays available for records your organization chooses to keep.',
        vi: 'Theo hồ sơ tổ chức đã khai, đây không phải nghĩa vụ của tổ chức bạn. Sổ vẫn sẵn sàng cho những hồ sơ tổ chức bạn muốn lưu giữ.',
    },
    /**
     * The first sentence of the page's profile-incomplete notice, which goes on
     * to name where in the product the profile is stated; a document has no
     * menu, so an export prints this sentence alone.
     */
    applicabilityProfileNotStated: {
        en: 'Whether this duty reaches your organization depends on your regulated entity type, which is not stated yet.',
        vi: 'Việc nghĩa vụ này có áp dụng cho tổ chức bạn hay không phụ thuộc vào loại hình tổ chức chịu quản lý, hiện chưa được khai.',
    },
    applicabilityNotDeclared: {
        en: 'The installed framework does not state who carries this duty, so the platform does not say whether it reaches your organization.',
        vi: 'Khung tuân thủ đã cài đặt không nêu ai mang nghĩa vụ này, nên nền tảng không nêu nghĩa vụ có áp dụng cho tổ chức bạn hay không.',
    },
    applicabilityReference: {
        en: 'The overlay that declares this register is installed for reference. The register is in use only when the overlay is installed directly; under a reference install the framework is read for reference and nothing is recorded here.',
        vi: 'Lớp phủ khai báo sổ này được cài đặt để tham khảo. Sổ chỉ được sử dụng khi lớp phủ được cài đặt trực tiếp; khi cài đặt để tham khảo, khung tuân thủ chỉ được đọc để tham khảo và không ghi nhận nội dung nào tại đây.',
    },
    clockNotLiveIntro: {
        en: 'An installed clock for this register does not drive a date yet. No date is derived from it until it is in force:',
        vi: 'Một mốc thời gian đã cài đặt cho sổ này chưa được dùng để tính ngày. Không có ngày nào được tính từ mốc này cho đến khi mốc có hiệu lực:',
    },
    // ── The held activity (the depositor lookup) ────────────────
    heldNotStarted: {
        en: 'The depositor lookup is held as not started: the implementing guidance has not been recorded. If the guidance has been issued since the installed framework was last checked, its own text governs.',
        vi: 'Việc tra cứu của khách hàng gửi tiền được coi là chưa bắt đầu: Hướng dẫn triển khai chưa được ghi nhận. Nếu Hướng dẫn đã được ban hành kể từ lần kiểm tra gần nhất của khung tuân thủ đã cài đặt, văn bản của Hướng dẫn được áp dụng.',
    },
    heldTracked: { en: 'Tracked item:', vi: 'Mục đang theo dõi:' },
    heldStartsOn: {
        en: 'The depositor lookup starts on {date}, as the recorded guidance states.',
        vi: 'Việc tra cứu của khách hàng gửi tiền bắt đầu từ ngày {date}, theo Hướng dẫn triển khai đã được ghi nhận.',
    },
    heldIssuedNoStart: {
        en: 'The implementing guidance has been recorded; it states no start date.',
        vi: 'Hướng dẫn triển khai đã được ghi nhận; Hướng dẫn không nêu ngày bắt đầu.',
    },
    // ── Boundary ────────────────────────────────────────────────
    boundaryHeading: {
        en: 'Scope and boundary — from your installed frameworks',
        vi: 'Phạm vi và ranh giới — theo khung tuân thủ đã cài đặt',
    },
    boundaryFrame: {
        en: 'These statements come from the frameworks installed for your organization, shown as each framework states them. The platform records what your organization did; it is not the Supervision Information System, does not connect to it, performs and dates no depositor lookup, verifies no mismatch, and shares, transmits or reports nothing.',
        vi: 'Các tuyên bố này đến từ khung tuân thủ đã cài đặt cho tổ chức bạn và được hiển thị đúng như từng khung nêu. Nền tảng ghi nhận những gì tổ chức bạn đã thực hiện; nền tảng không phải là Hệ thống thông tin giám sát, không kết nối tới Hệ thống, không thực hiện và không ấn định ngày cho việc tra cứu của khách hàng gửi tiền, không xác minh thông tin chưa khớp, và không chia sẻ, không truyền, không báo cáo bất cứ điều gì.',
    },
    noBoundaryStatements: {
        en: 'No installed framework declares a boundary statement for this register. This describes the content installed for your organization, not your organization’s scope.',
        vi: 'Không có khung tuân thủ đã cài đặt nào khai báo tuyên bố về ranh giới cho sổ này. Câu này mô tả nội dung đã cài đặt cho tổ chức bạn, không phải phạm vi của tổ chức bạn.',
    },
    // ── Due state (a branch requirement or request) ─────────────
    colDueState: { en: 'Due', vi: 'Thời hạn' },
    dueNoPeriod: {
        en: 'The instrument states no period; the requirement states none.',
        vi: 'Văn bản không quy định thời hạn; yêu cầu cũng không nêu thời hạn.',
    },
    dueOpen: { en: 'Due on {date}', vi: 'Hạn ngày {date}' },
    dueOverdue: { en: 'Overdue since {date}', vi: 'Quá hạn từ ngày {date}' },
    dueAnswered: { en: 'Answered on {date}', vi: 'Đã đáp ứng ngày {date}' },
    dueAnsweredWithDue: {
        en: 'Answered on {date} (due on {due})',
        vi: 'Đã đáp ứng ngày {date} (hạn ngày {due})',
    },
    // ── Branch requirement ──────────────────────────────────────
    colRequirement: { en: 'Requirement', vi: 'Yêu cầu' },
    fieldAsksFor: { en: 'What the branch asks for', vi: 'Nội dung chi nhánh yêu cầu' },
    asksPublicity: { en: 'Notice and publicity', vi: 'Thông báo, công khai' },
    asksInformation: { en: 'Information to provide', vi: 'Cung cấp thông tin' },
    publicityCompletedOn: {
        en: 'Publicity recorded complete on {date} by {person}',
        vi: 'Đã ghi nhận hoàn thành công khai ngày {date}, người ghi nhận: {person}',
    },
    publicityNotCompleted: {
        en: 'Completion not recorded',
        vi: 'Chưa ghi nhận hoàn thành',
    },
    // ── Register (b) — channel coverage ─────────────────────────
    colCoverage: { en: 'Channels', vi: 'Các kênh' },
    coverageRecorded: { en: 'recorded', vi: 'đã ghi nhận' },
    coverageAbsence: {
        en: 'stated the fund has none',
        vi: 'quỹ nêu là không có',
    },
    coverageNotRecorded: { en: 'not recorded', vi: 'chưa ghi nhận' },
    // ── Register (c) — information requests ─────────────────────
    colRequestCount: { en: 'Requests', vi: 'Số yêu cầu' },
    providedOn: { en: 'Provided on {date}', vi: 'Đã cung cấp ngày {date}' },
    // ── Register (d) — the guidance state when an enquiry was logged ──
    colGuidanceState: { en: 'Guidance at the time', vi: 'Hướng dẫn tại thời điểm đó' },
    guidanceHeld: {
        en: 'Lookup held as not started — guidance not recorded',
        vi: 'Việc tra cứu được coi là chưa bắt đầu — Hướng dẫn chưa được ghi nhận',
    },
    guidanceIssued: { en: 'Guidance recorded', vi: 'Hướng dẫn đã được ghi nhận' },
    guidanceNotDeclared: {
        en: 'No tracked guidance declared',
        vi: 'Không có hướng dẫn được khai báo để theo dõi',
    },
    // ── Register (a) — the personal-data provision ──────────────
    colMechanism: { en: 'Personal-data provision', vi: 'Quy định về dữ liệu cá nhân' },
    mechanismRecorded: { en: 'recorded', vi: 'đã ghi nhận' },
    mechanismNotRecorded: { en: 'not recorded', vi: 'chưa ghi nhận' },
    mechanismNotApplicable: {
        en: 'not applicable (no personal data)',
        vi: 'không áp dụng (không có dữ liệu cá nhân)',
    },
    approvalRef: { en: 'Approval: {ref}', vi: 'Phê duyệt: {ref}' },
    /**
     * Sprint 62.50.1 — a written request with no answer recorded. The instrument
     * states no period, so it is never pending, awaited or overdue.
     */
    answerNotRecorded: { en: 'No answer recorded', vi: 'Chưa ghi nhận kết quả' },
    // ── Reporting calendar ──────────────────────────────────────
    colEffectiveDeadline: { en: 'Effective deadline', vi: 'Hạn áp dụng' },
    colSubmission: { en: 'Submission', vi: 'Tình trạng gửi' },
    movedToNextWorkingDay: {
        en: 'moved to the next working day',
        vi: 'đã chuyển sang ngày làm việc tiếp theo',
    },
    submissionSubmitted: { en: 'Submitted on {date}', vi: 'Đã gửi ngày {date}' },
    submissionOpen: { en: 'Not yet submitted', vi: 'Chưa gửi' },
    submissionOverdue: { en: 'Not submitted by the deadline', vi: 'Chưa gửi trước hạn' },
    // ── Incidents ───────────────────────────────────────────────
    colResumption: { en: 'Resumption', vi: 'Gửi lại' },
    resumeNotFixed: { en: 'Not fixed yet', vi: 'Chưa khắc phục' },
    resumeAwaiting: { en: 'Awaiting resumption — resume by {date}', vi: 'Chờ gửi lại — hạn {date}' },
    resumeWithin: { en: 'Resumed within the period', vi: 'Đã gửi lại trong thời hạn' },
    resumeAfter: { en: 'Resumed after the period', vi: 'Đã gửi lại sau thời hạn' },
    resumeNoClock: {
        en: 'No installed clock states a resumption period',
        vi: 'Không có mốc thời gian đã cài đặt nêu thời hạn gửi lại',
    },
    // ── Error and query notices ─────────────────────────────────
    colResend: { en: 'Resend', vi: 'Gửi lại' },
    colLock: { en: 'Lock', vi: 'Khóa' },
    resendOnTime: { en: 'Resent on time', vi: 'Đã gửi lại đúng hạn' },
    resendLate: { en: 'Resent after the deadline', vi: 'Đã gửi lại sau hạn' },
    resendOpen: { en: 'Resend due on {date}', vi: 'Hạn gửi lại ngày {date}' },
    resendOverdue: { en: 'Not resent by {date}', vi: 'Chưa gửi lại trước ngày {date}' },
    resendNoClock: {
        en: 'No installed clock states a resend period for this notice type',
        vi: 'Không có mốc thời gian đã cài đặt nêu thời hạn gửi lại cho loại thông báo này',
    },
    lockNone: { en: 'Not locked', vi: 'Không bị khóa' },
    lockLocked: { en: 'Locked', vi: 'Bị khóa' },
    lockUnlockRequested: { en: 'Unlock requested: {ref}', vi: 'Đã đề nghị mở khóa: {ref}' },
    lockNoDecision: { en: 'no decision recorded', vi: 'chưa ghi nhận quyết định' },
    // ── System start ────────────────────────────────────────────
    groupNotRecorded: { en: 'Not recorded', vi: 'Chưa ghi nhận' },
    systemStartNone: {
        en: 'Nothing is recorded yet: both dates read not recorded.',
        vi: 'Chưa ghi nhận gì: cả hai ngày đều chưa ghi nhận.',
    },
    transitionalInForce: {
        en: 'Transitional reporting rule: in force until your organization records that it was told the System operates.',
        vi: 'Quy định báo cáo chuyển tiếp: còn áp dụng cho đến khi tổ chức bạn ghi nhận đã được thông báo Hệ thống vận hành.',
    },
    transitionalEnded: {
        en: 'Transitional reporting rule: ended on the date your organization was told the System operates.',
        vi: 'Quy định báo cáo chuyển tiếp: chấm dứt từ ngày tổ chức bạn được thông báo Hệ thống vận hành.',
    },
    upgradeNotStarted: {
        en: 'Upgrade: not started — the official-operation date is not recorded.',
        vi: 'Nâng cấp: chưa bắt đầu — chưa ghi nhận ngày chính thức đi vào hoạt động.',
    },
    upgradeNotRequired: {
        en: 'Upgrade: not required on what your organization recorded.',
        vi: 'Nâng cấp: không cần theo những gì tổ chức bạn đã ghi nhận.',
    },
    upgradeOpen: { en: 'Upgrade: due on {date}.', vi: 'Nâng cấp: hạn ngày {date}.' },
    upgradeOverdue: {
        en: 'Upgrade: not completed by {date}.',
        vi: 'Nâng cấp: chưa hoàn thành trước ngày {date}.',
    },
    upgradeCompleted: {
        en: 'Upgrade: completed on {date}.',
        vi: 'Nâng cấp: đã hoàn thành ngày {date}.',
    },
    upgradeNoClock: {
        en: 'Upgrade: no installed clock states an upgrade period.',
        vi: 'Nâng cấp: không có mốc thời gian đã cài đặt nêu thời hạn nâng cấp.',
    },
    // ── A section out of scope ──────────────────────────────────
    sectionNotOffered: {
        en: 'Not offered: on your organization’s stated profile, this section is not your organization’s duty.',
        vi: 'Không áp dụng: theo hồ sơ tổ chức đã khai, mục này không phải nghĩa vụ của tổ chức bạn.',
    },
    // ── Evidence files ──────────────────────────────────────────
    evidenceFiles: { en: 'Evidence files', vi: 'Tệp tài liệu chứng minh' },
    evidenceFileHash: { en: 'SHA-256 {hash}', vi: 'SHA-256 {hash}' },
    evidenceFileNotHeld: { en: 'File no longer held', vi: 'Tệp không còn được lưu giữ' },
    evidenceFilesNone: { en: 'No file is bound yet.', vi: 'Chưa gắn tệp nào.' },
    // ── Table headings a page and its export share ──────────────
    sharedRegisterHeading: { en: 'Information received', vi: 'Thông tin đã nhận' },
    publicityRegisterHeading: {
        en: 'Branch requirements and channel coverage',
        vi: 'Yêu cầu của chi nhánh và mức bao phủ theo kênh',
    },
    publicityRecordsHeading: { en: 'Channel records', vi: 'Hồ sơ theo kênh' },
    requestRequirementsHeading: { en: 'Branch requirements', vi: 'Các yêu cầu của chi nhánh' },
    requestsRegisterHeading: { en: 'Information requests', vi: 'Các yêu cầu cung cấp thông tin' },
    // ── Export frame (Sprint 62.50.1) ───────────────────────────
    exportOrganization: { en: 'Organization: {name}', vi: 'Tổ chức: {name}' },
    exportGeneratedAt: { en: 'Generated at: {instant}', vi: 'Thời điểm tạo: {instant}' },
    exportGeneratedBy: { en: 'Generated by: {name}', vi: 'Người tạo: {name}' },
    exportFramework: {
        en: 'Installed framework: {code}, pack version {version}',
        vi: 'Khung tuân thủ đã cài đặt: {code}, phiên bản gói {version}',
    },
    exportRecordCount: { en: 'Records: {count}', vi: 'Số hồ sơ: {count}' },
    exportField: { en: 'Field', vi: 'Trường' },
    exportValue: { en: 'Value', vi: 'Giá trị' },
    exportState: { en: 'State', vi: 'Tình trạng' },
    /** The frame every export carries: it is a copy of what the organisation recorded, sent to no one. */
    exportFrame: {
        en: 'A copy of what your organization recorded in this register, as the register shows it. The platform sends this document to no one.',
        vi: 'Bản sao những gì tổ chức bạn đã ghi nhận trong sổ này, đúng như sổ hiển thị. Nền tảng không gửi tài liệu này cho bất kỳ ai.',
    },
};
/**
 * One word in `locale` (`vi`, `vi-VN` → VI; anything else → EN), with each
 * `{name}` filled from `values`. A placeholder with no value stays as written,
 * so a missing value is visible rather than silently dropped.
 */
export function pcfSupervisionWord(key, locale, values = {}) {
    const pair = PCF_SUPERVISION_WORDS[key];
    const text = locale === 'vi' || locale.startsWith('vi-') ? pair.vi : pair.en;
    return text.replace(/\{(\w+)\}/g, (whole, name) => values[name] ?? whole);
}
//# sourceMappingURL=pcf-supervision-words.js.map